import { useMemo } from 'react';
import {
  useApi,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';

type MarketplaceFetchApi = {
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
};

function createMarketplaceHeaders(userEntityRef: string, email?: string) {
  const headers: Record<string, string> = {};

  if (userEntityRef) {
    headers['x-user-ref'] = userEntityRef;
    const userLogin = userEntityRef.split('/').pop() ?? '';
    if (userLogin) {
      headers['x-user-login'] = userLogin;
    }
  }

  if (email) {
    headers['x-user-email'] = email;
  }

  return headers;
}

function mergeHeaders(
  marketplaceHeaders: Record<string, string>,
  initHeaders?: HeadersInit,
): Record<string, string> {
  const headers = { ...marketplaceHeaders };

  if (!initHeaders) {
    return headers;
  }

  if (initHeaders instanceof Headers) {
    initHeaders.forEach((value, key) => {
      headers[key] = value;
    });
    return headers;
  }

  if (Array.isArray(initHeaders)) {
    for (const [key, value] of initHeaders) {
      headers[key] = value;
    }
    return headers;
  }

  return {
    ...headers,
    ...initHeaders,
  };
}

export function useMarketplaceFetchApi(): MarketplaceFetchApi {
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const getBackstageIdentity = identityApi.getBackstageIdentity;
  const getProfileInfo =
    (identityApi as { getProfileInfo?: () => Promise<{ email?: string } | null> }).getProfileInfo;

  return useMemo(() => ({
    fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
      const identityPromise = getBackstageIdentity
        ? getBackstageIdentity().catch(() => null)
        : Promise.resolve(null);
      const profilePromise = getProfileInfo
        ? getProfileInfo().catch(() => null)
        : Promise.resolve(null);
      const [identity, profile] = await Promise.all([identityPromise, profilePromise]);

      const headers = createMarketplaceHeaders(
        identity?.userEntityRef ?? '',
        profile?.email?.trim() || undefined,
      );

      const mergedHeaders = mergeHeaders(headers, init?.headers);
      const finalInit = Object.keys(mergedHeaders).length > 0
        ? { ...init, headers: mergedHeaders }
        : init;

      if (finalInit) {
        return fetchApi.fetch(input, finalInit);
      }

      return fetchApi.fetch(input);
    },
  }), [fetchApi, getBackstageIdentity, getProfileInfo]);
}